import { Flex, Select } from 'antd';
import type { SelectProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlaCurrencyIcon } from '@/app/landing/sla/shared/components/SlaCurrencyIcon';
import { SLA_CURRENCY_CODES } from '~/app/landing/sla/shared/utils/sla-currency.utils';

type Props = Omit<SelectProps, 'options'> & {
  /** Selected value shows icon only — for narrow table cells */
  compact?: boolean;
};

function SlaCurrencyOption({ code, label }: { code: string; label: string }) {
  return (
    <Flex align="center" gap={8}>
      <SlaCurrencyIcon code={code} size={14} title={label} />
      <span>{label}</span>
    </Flex>
  );
}

export function SlaCurrencySelect({ compact = false, ...selectProps }: Props) {
  const { t } = useTranslation('sla');

  const options = useMemo(
    () =>
      SLA_CURRENCY_CODES.map((code) => ({
        value: code,
        label: t(`currency.${code}`, code),
      })),
    [t],
  );

  return (
    <Select
      {...selectProps}
      options={options}
      optionRender={(option) => (
        <SlaCurrencyOption
          code={String(option.value)}
          label={String(option.label ?? option.value)}
        />
      )}
      labelRender={(option) => {
        const code = String(option.value);
        const label = String(option.label ?? code);

        if (compact) {
          return <SlaCurrencyIcon code={code} size={16} title={label} />;
        }

        return <SlaCurrencyOption code={code} label={label} />;
      }}
    />
  );
}
